import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { NAV_ITEMS } from './navigation';
import { can } from '@/lib/permissions';
import type { Role } from '@/types/domain';
import { cn } from '@/lib/cn';

const GROUPS: { id: 'main' | 'finance' | 'system'; label: string }[] = [
  { id: 'main', label: 'Operación' },
  { id: 'finance', label: 'Finanzas' },
  { id: 'system', label: 'Sistema' },
];

/** Premium glass sidebar with role-aware navigation. */
export function Sidebar({ role }: { role: Role }) {
  const { t } = useTranslation();

  return (
    <aside className="hidden w-72 shrink-0 flex-col gap-6 p-4 lg:flex">
      <div className="flex items-center gap-3 px-2 py-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-gradient shadow-glow">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-display text-lg font-bold leading-none text-gradient">VaulTex</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{t('brand.slogan')}</p>
        </div>
      </div>

      <nav className="glass flex-1 space-y-5 overflow-y-auto rounded-2xl p-3">
        {GROUPS.map((group) => {
          const items = NAV_ITEMS.filter(
            (i) => i.group === group.id && (!i.capability || can(role, i.capability)),
          );
          if (items.length === 0) return null;
          return (
            <div key={group.id}>
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {group.label}
              </p>
              <ul className="space-y-1">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors',
                          isActive
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <motion.span
                              layoutId="nav-active"
                              className="absolute inset-0 -z-10 rounded-xl bg-brand-gradient/15 ring-1 ring-primary/30"
                              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                            />
                          )}
                          <item.icon className="h-[18px] w-[18px]" />
                          <span>{t(item.labelKey)}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
